import {
  Overlay,
  OverlayPositionBuilder,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { GlobalUtilsService } from 'src/app/global-utils.service';
import { ContactFormFocusInteractorService } from '../contact-form-focus-interactor.service';
import { FormControlName, SelectInputOption } from '../contact-form-types';
import { ContactFormService } from '../contact-form.service';
import { InputSelectOptionsComponent } from './input-select-options.component';

@Component({
  selector: 'app-input-select',
  template: `
    <div
      #inputContainer
      class="input-container"
      (click)="onFocus($event)"
      tabindex="0"
      cdkTrapFocus
      (focus)="onFocus($event)"
      (keydown.Tab)="testing()"
      [ngClass]="{
        disabled: isDisabled,
        isFocused: isFocused,
        tablet: globalUtilsService.isTablet(),
        mobile: globalUtilsService.isMobile()
      }"
    >
      <span
        class="label"
        [ngClass]="{
          'on-top': control.value,
          'is-focused': isFocused && !control.value,
          invalid: control.invalid && control.touched && !isFocused
        }"
        >{{ isFocused || control.value ? label : placeholder }}</span
      >
      <div class="input-control-container">
        <span class="arrow"></span>
        <p
          class="input-control-deactivate"
          *ngIf="control.value"
          [ngClass]="{ invalid: control.invalid && control.touched }"
          style=" text-transform: capitalize; "
        >
          {{ control.value }}
        </p>
      </div>
    </div>
  `,
  styleUrls: ['../input-control.scss'],
  styles: [
    `
      .input-container {
        cursor: pointer !important;
        outline: 0;
      }

      .arrow {
        display: block;
        position: absolute;
        width: 0.2rem;
        height: 0.2rem;
        border-left: 1px solid #888888;
        border-bottom: 1px solid #888888;
        transform: rotate(-45deg);
        right: 0.17rem;
        top: 0.16rem;
        transition: all 300ms cubic-bezier(0.19, 1, 0.22, 1);
      }

      .input-container.isFocused .arrow {
        transform: rotate(-225deg);
        top: 0.27rem;
        transition: all 500ms cubic-bezier(0.19, 1, 0.22, 1);
      }

      .input-container.disabled .arrow {
        border-left: 1px solid #888888;
        border-bottom: 1px solid #888888;
      }

      .input-container.isFocused .input-control-container {
        border-bottom: 1px solid white !important;
      }

      .input-container.isFocused .label {
        color: #13a538;
      }

      .input-container.isFocused .arrow {
        border-left: 1px solid #13a538;
        border-bottom: 1px solid #13a538;
      }

      .input-container.tablet .arrow {
        width: 0.38rem;
        height: 0.38rem;
        top: 0.2rem;
      }

      .input-container.tablet.isFocused .arrow {
        top: 0.3rem;
      }

      .input-container.mobile .arrow {
        width: 0.38rem;
        height: 0.38rem;
        top: 0.1rem;
      }

      .input-container.mobile.isFocused .arrow {
        top: 0.26rem;
      }
    `,
  ],
})
export class InputSelectComponent implements OnInit, AfterViewInit {
  // tslint:disable-next-line: no-output-native
  @Output()
  focusBlur: EventEmitter<boolean>;

  @Input()
  label: string;

  @Input()
  placeholder: string;

  @Input()
  controlName: FormControlName;

  @Input()
  control: FormControl;

  @Input()
  options: SelectInputOption[];

  @ViewChild('inputContainer', { static: true })
  inputContainer: ElementRef;

  isOptionsOpen: boolean;
  isDisabled: boolean;

  /**
   * Here, *isFocused* means that the component to select
   * an option is open waiting for user decision.
   */
  isFocused: boolean;

  private overlayRef: OverlayRef;
  private portal: ComponentPortal<InputSelectOptionsComponent>;
  private selectOptionsComponentRef: ComponentRef<InputSelectOptionsComponent>;

  constructor(
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
    public globalUtilsService: GlobalUtilsService,
    private contactFormFocusInteractorService: ContactFormFocusInteractorService,
    private contactFormService: ContactFormService
  ) {
    this.isOptionsOpen = false;
  }

  ngOnInit() {
    this.isDisabled = this.contactFormService.disabledInputs[
      this.controlName
    ].value;
    this.contactFormService.disabledInputs[this.controlName].subscribe(
      (isDisabled) => {
        this.isDisabled = isDisabled;
      }
    );
  }

  ngAfterViewInit() {
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      positionStrategy: this.overlayPositionBuilder
        .flexibleConnectedTo(this.inputContainer)
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
        ]),

      width: this.getWidthOfInputContainer(),
    });

    this.overlayRef.backdropClick().subscribe(() => {
      if (this.overlayRef.hasAttached()) {
        this.defocus();
      }
    });

    this.portal = new ComponentPortal(InputSelectOptionsComponent);
  }

  @HostListener('window:keyup.escape')
  onKeyupEscape() {
    if (this.isDisabled && !this.isFocused) {
      return;
    }
    this.defocus();
  }

  @HostListener('window:resize')
  onResize() {
    this.overlayRef.updateSize({
      width: this.getWidthOfInputContainer(),
    });
  }

  testing() {
    setTimeout(() => {
      // AQUI VEM O COMANDO PARA MUDAR FOCO
      this.contactFormFocusInteractorService.nextElementFromTabEvent(
        this.controlName
      );
      this.defocus();
    }, 0);
  }

  private getWidthOfInputContainer() {
    return (this.inputContainer.nativeElement as HTMLElement).offsetWidth;
  }

  private openSelectOptionsComponent() {
    if (!this.overlayRef.hasAttached()) {
      this.selectOptionsComponentRef = this.overlayRef.attach(this.portal);
      this.selectOptionsComponentRef.instance.options = this.options;
    }
  }

  private defocus(value?: SelectInputOption) {
    if (value) {
      this.control.patchValue(value);
    }
    this.overlayRef.detach();
    this.selectOptionsComponentRef = undefined;
    this.isFocused = false;
  }

  onFocus(e) {
    if (this.isDisabled) {
      return;
    }
    this.openSelectOptionsComponent();
    this.selectOptionsComponentRef.instance.optionSelected.subscribe(
      (option) => {
        this.defocus(option);
        setTimeout(() => {
          this.testing();
        }, 250);
      }
    );
    this.isFocused = true;
  }
}
